/**
 * RFC 9457 problems, as this app handles them.
 *
 * `error_code` IS THE CONTRACT AND `title`/`detail` ARE NOT — the generated `Problem` says so in
 * as many words: "The machine code a client branches on. This is the contract; `title` and
 * `detail` are not." So every branch in a screen reads `error_code`, and `detail` is only ever
 * shown to a human.
 *
 * THE 429 SAYS HOW LONG TO WAIT. authors-api's twenty 429 responses all `$ref` the shared
 * `Problem429`, which declares a required integer `Retry-After` since decision-log row 220 (row
 * 170: every 429 sends one). `retryAfterOf` at the end of this file is the one reader of that
 * header for every screen client but the sign-in's own (the cabinet, editor and calendar clients,
 * the application flow, the support pages), and `tooManyRequests` the one sentence all of them
 * show for it: "Too many requests just now. Try again in 30 seconds." A 429 with no readable
 * header keeps the screen's own sentence from before. Nothing counts down or disables a button.
 *
 * THE SENTENCES ARE THE CATALOGUE'S (`cabinet` namespace, `problem.*`; design 29, W5), read when a
 * function here is called - on a render - so they follow the cabinet's language. A refusal whose
 * code has no catalogue sentence still shows the server's English `detail`.
 */
import { ENGLISH_RESOURCES, i18n } from '../../../i18n';
import type { ErrorCode, Problem } from './generated';

/** Codes these screens actually branch on, each verified present in the generated types. */
export const ERROR_CODE = {
  /** getPaymentsView 403, replyToComment 403: payout actions are refused, the cabinet stays up. */
  authorDisabled: 'AUTHOR_DISABLED',
  /** replyToComment 403: the comment is not on one of this author's articles. */
  notArticleOwner: 'NOT_ARTICLE_OWNER',
  /** replyToComment 409: a reply may not have a reply. */
  replyHasNoReplies: 'REPLY_HAS_NO_REPLIES',
  /** deleteNeverPublishedDraft 409. */
  deleteOnlyNeverPublishedDraft: 'DELETE_ONLY_NEVER_PUBLISHED_DRAFT',
  /** unarchiveOwnArticle 403: staff archived it, so the author may not put it back. */
  archiveActorMismatch: 'ARCHIVE_ACTOR_MISMATCH',
  /** pullScheduledArticle 409: go-live passed while the author was deciding. */
  goLiveAlreadyPassed: 'GO_LIVE_ALREADY_PASSED',
  /** withdrawArticle 409. */
  verdictAlreadyRecorded: 'VERDICT_ALREADY_RECORDED',
  /** createDraft 403: no author row (design 25) - an applicant is refused another code, below. */
  authorCapabilityRequired: 'AUTHOR_CAPABILITY_REQUIRED',
  /** archiveOwnArticle / unarchiveOwnArticle 409: somebody moved the article mid-flight. */
  wrongFromState: 'WRONG_FROM_STATE',
} as const satisfies Readonly<Record<string, ErrorCode>>;

export class ApiProblem extends Error {
  override readonly name = 'ApiProblem';

  readonly status: number;
  readonly errorCode: string;
  readonly detail: string | undefined;
  /** `Retry-After` in seconds, when the answer carried one `retryAfterOf` (below) could read. */
  readonly retryAfterSeconds: number | undefined;

  constructor(problem: Problem, retryAfterSeconds?: number) {
    super(problem.title);
    this.status = problem.status;
    this.errorCode = problem.error_code;
    this.detail = problem.detail;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * A failure that never reached the API at all — DNS, TLS, an offline laptop, a CORS refusal.
 *
 * It is a DIFFERENT THING from a problem document and must read differently on screen: "we could
 * not reach MoonX" is not "MoonX refused you", and telling an author the second when
 * the first happened sends them to support for nothing.
 */
export class TransportFailure extends Error {
  override readonly name = 'TransportFailure';
}

/**
 * A 404 THAT THE CONTRACT CANNOT SPELL.
 *
 * ★ CORRECTED 2026-09-20: THIS USED TO SAY THE ENUM HAD NO NOT-FOUND MEMBER. `Problem.error_code`
 * is required and is the `ErrorCode` enum (shared.yaml), which has since grown from 46 members to
 * **65** and now carries a plain `NOT_FOUND`, alongside `CONFLICT_UNSPECIFIED` and
 * `ARTICLE_NOT_WRITABLE`. `UPLOAD_OBJECT_NOT_FOUND` is still upload-specific. So a server CAN put a
 * code in a 404 body and a client CAN branch on it: the gap this class was built around is CLOSED,
 * and what it still covers is a 404 that arrives with no problem document at all.
 *
 * For that bare 404 this class is how the cabinet copes without pretending: its own failure kind,
 * carrying no `error_code` at all, because inventing a code for a body that carried none would put
 * a value on screen that the API did not send.
 */
export class NotFoundFailure extends Error {
  override readonly name = 'NotFoundFailure';
  /** The catalogue's sentence for this absence (`problem.notFound.*`); without one, `message`. */
  readonly says: NotFoundWords | undefined;

  constructor(message: string, says?: NotFoundWords) {
    super(message);
    this.says = says;
  }
}

export type NotFoundWords = keyof (typeof ENGLISH_RESOURCES)['cabinet']['problem']['notFound'];

export function isApiProblem(error: unknown): error is ApiProblem {
  return error instanceof ApiProblem;
}

export function hasErrorCode(error: unknown, code: string): boolean {
  return isApiProblem(error) && error.errorCode === code;
}

/** A code the catalogue words itself (`problem.codes.*`). */
type WordedCode = keyof (typeof ENGLISH_RESOURCES)['cabinet']['problem']['codes'];

function isWordedCode(code: string): code is WordedCode {
  return Object.hasOwn(ENGLISH_RESOURCES.cabinet.problem.codes, code);
}

/**
 * Which piece a refusal is about. On a video's screens a code worded for an article reads its
 * `_video` variant (`problem.codes.WRONG_FROM_STATE_video`); a code with none reads as it is. The
 * same values as the editor's `PieceContext`, stated here so this layer imports no screen.
 */
export type ProblemContext = '' | 'video';

/**
 * The sentence an author reads. Deliberately not derived from `title`: the server's title is a
 * developer-facing summary and may name a state machine, while the catalogue words the codes the
 * cabinet's own screens can actually meet in plain language. Anything else falls through to
 * `detail`, which the contract scopes to "only the caller's own row" - and which stays English.
 */
export function describeProblem(error: unknown, context: ProblemContext = ''): string {
  const t = i18n.getFixedT(null, 'cabinet');
  if (error instanceof TransportFailure) {
    return t('problem.transport');
  }
  if (error instanceof NotFoundFailure) {
    return error.says === undefined ? error.message : t(`problem.notFound.${error.says}`);
  }
  if (!isApiProblem(error)) {
    return t('problem.unknown');
  }
  if (isWordedCode(error.errorCode)) {
    const variant = `${error.errorCode}_${context}`;
    return context !== '' && isWordedCode(variant)
      ? t(`problem.codes.${variant}`)
      : t(`problem.codes.${error.errorCode}`);
  }
  if (error.status === 429) {
    // The wait, when the 429 carried one (see the header comment); no countdown either way.
    return tooManyRequests(error.retryAfterSeconds, t('problem.tooManyRequests'));
  }
  if (error.status >= 500) {
    return t('problem.serverTrouble');
  }
  return error.detail ?? error.message;
}

/**
 * `Retry-After` in whole seconds, or `undefined` when there is none this app can read.
 *
 * BOTH OF RFC 9110 § 10.2.3's FORMS. The platform sends delay-seconds and nothing else
 * (`Problem429`, docs/design/07-api/shared.yaml), so that is the form every real 429 takes. An
 * HTTP-date can still come from something standing in front of the API, and it is measured against
 * the same response's own `Date` header, never against this machine's clock: the app reads no
 * clock (C11), and two instants from one server cannot disagree the way a laptop's clock can with
 * either. No readable `Date` means no wait, and so the screen's own sentence - and a cross-origin
 * response hides `Date` today, since it is not among the headers the API exposes
 * (`AUTHORS_API_CORS_EXPOSED_HEADERS`, src/common/http/cors-policy.ts). A date already past is 0.
 *
 * The sign-in's own reader (`parseRetryAfterSeconds`, ../../auth/api/failure.ts) takes
 * delay-seconds only, on purpose, and that is not a disagreement: its number schedules a retry by
 * itself, where this one only words a sentence and, for the address check, times the next ask.
 */
export function retryAfterOf(headers: Headers): number | undefined {
  const raw = headers.get('Retry-After')?.trim() ?? '';
  if (/^\d+$/.test(raw)) {
    return Number.parseInt(raw, 10);
  }
  const until = Date.parse(raw);
  const now = Date.parse(headers.get('Date') ?? '');
  if (Number.isNaN(until) || Number.isNaN(now)) {
    return undefined;
  }
  return Math.max(0, Math.ceil((until - now) / 1000));
}

/**
 * THE 429 SENTENCE, ONCE FOR THE WHOLE APP: "Too many requests just now. Try again in 30 seconds."
 * From a minute up the wait is said in minutes ROUNDED UP, so the sentence never sends anybody back
 * before the limit's window has opened. Minutes are as far as it goes: the seeded windows are an hour
 * (`rate_limit_policy.default_window_s`, 3600).
 *
 * `fallback` is the calling screen's own sentence for a 429 that named no wait - no header, one
 * `retryAfterOf` could not read, or zero - so such a screen reads exactly as it did before the
 * number was shown anywhere.
 */
export function tooManyRequests(retryAfterSeconds: number | undefined, fallback: string): string {
  if (retryAfterSeconds === undefined || retryAfterSeconds < 1) {
    return fallback;
  }
  const seconds = Math.ceil(retryAfterSeconds);
  const t = i18n.getFixedT(null, 'cabinet');
  return seconds < 60
    ? t('problem.waitSeconds', { count: seconds })
    : t('problem.waitMinutes', { count: Math.ceil(seconds / 60) });
}

/**
 * ★ DESIGN 25'S TWO REFUSALS FOR AN APPLICANT - an author row not approved yet (section 3.4, P14
 * and P15). `AUTHOR_APPROVAL_REQUIRED`: submitting, resubmitting, withdrawing, pulling, archiving, a
 * preview link or a reply, before approval. `APPLICANT_IMAGE_LIMIT_REACHED`: an applicant's 51st
 * article picture (their pictures are public objects the moment they are claimed).
 */
export const APPLICANT_ERROR_CODE = {
  authorApprovalRequired: 'AUTHOR_APPROVAL_REQUIRED',
  applicantImageLimitReached: 'APPLICANT_IMAGE_LIMIT_REACHED',
} as const satisfies Readonly<Record<string, ErrorCode>>;

/**
 * ★ DESIGN 28'S REFUSAL FOR THE DELETION (decision 5): `BALANCE_FORFEIT_CONFIRMATION_REQUIRED` is
 * `requestAccountDeletion`'s 422 while the balance is above $0.00 and the request did not set
 * `forfeitBalance` - the balance is lost with the account. At the foot, as the applicant codes are.
 */
export const DELETION_ERROR_CODE = {
  balanceForfeitConfirmationRequired: 'BALANCE_FORFEIT_CONFIRMATION_REQUIRED',
} as const satisfies Readonly<Record<string, ErrorCode>>;
